<template>
  <div
    class="w-full bg-gradient-to-r from-blue-50 via-green-50 to-purple-50 border-b border-gray-200 overflow-x-hidden">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16 py-3 min-w-0">
        <div class="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
          <!-- Enhanced Global Currency Indicator -->
          <div class="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <div class="relative flex-shrink-0">
              <div
                class="rounded-full w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white text-xs sm:text-sm font-medium shadow-sm">
                {{ selectedCurrency?.flag || '🇰🇪' }}
              </div>
              <!-- Global indicator pulse -->
              <div
                class="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full animate-pulse shadow-sm">
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-xs sm:text-sm font-semibold text-gray-800 flex items-center">
                <span class="hidden sm:inline">🌍 Global Currency</span>
                <span class="sm:hidden">🌍 Currency</span>
                <span
                  class="ml-1 sm:ml-2 inline-flex items-center px-1 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Live
                </span>
              </div>
              <div class="text-xs text-gray-600 hidden sm:block truncate">
                {{ isDetectingLocation ? 'Detecting location...' : 'All tools & calculations update instantly' }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
          <!-- Enhanced Current Currency Display -->
          <div
            class="bg-white/90 backdrop-blur-sm rounded-lg px-2 sm:px-4 py-1 sm:py-2 shadow-sm border border-white/50 transition-all duration-300 hover:shadow-md">
            <div class="flex items-center space-x-1 sm:space-x-2">
              <div class="relative">
                <span class="text-sm sm:text-lg">{{ selectedCurrency?.flag || '🇰🇪' }}</span>
                <div v-if="isDetectedFromLocation" class="absolute -top-1 -right-1 w-2 h-2 bg-blue-400 rounded-full"
                  title="Auto-detected from location"></div>
              </div>
              <div>
                <div class="text-xs sm:text-sm font-bold text-gray-800">{{ selectedCurrency?.code || 'KES' }}</div>
                <div class="text-xs text-gray-600 hidden sm:block">{{ selectedCurrency?.name || 'Kenyan Shilling' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Enhanced Quick Currency Buttons -->
          <div class="hidden lg:flex items-center space-x-1 bg-white/60 backdrop-blur-sm rounded-lg p-1 shadow-sm">
            <button v-for="c in quickList" :key="c.code" @click="selectQuick(c)" :disabled="isLoading" :class="[
              'px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 transform hover:scale-105',
              selectedCurrency?.code === c.code
                ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-md'
                : 'bg-white/80 text-gray-700 hover:bg-white hover:shadow-sm',
              isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            ]">
              <div class="flex items-center space-x-1">
                <span>{{ c.flag }}</span>
                <span>{{ c.code }}</span>
              </div>
            </button>
          </div>

          <!-- More Currencies Button -->
          <div class="relative" ref="selectorAnchor">
            <button @click="toggleSelector" :disabled="isLoading"
              class="inline-flex items-center px-2 sm:px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-500 transition-colors duration-200 disabled:opacity-50 whitespace-nowrap">
              <span v-if="isLoading" class="mr-1">
                <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                  </path>
                </svg>
              </span>
              {{ isLoading ? 'Loading...' : 'More' }}
            </button>

            <!-- Teleported Currency Selector Dropdown -->
            <teleport to="body">
              <transition name="dropdown">
                <div v-if="showSelector" ref="dropdownRef" :style="dropdownStyle"
                  class="bg-white rounded-lg shadow-lg border border-gray-200">
                  <div class="p-3" style="max-width: 100%">
                    <div class="text-xs font-medium text-gray-700 mb-2">Select Currency</div>
                    <div class="max-h-48 overflow-y-auto">
                      <button v-for="currency in availableCurrencies" :key="currency.code"
                        @click="selectCurrency(currency)" :class="[
                          'w-full text-left px-3 py-2 rounded text-xs hover:bg-gray-50 transition-colors duration-150',
                          selectedCurrency?.code === currency.code ? 'bg-green-50 text-green-700' : 'text-gray-700'
                        ]">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center">
                            <span class="mr-2">{{ currency.flag }}</span>
                            <span class="font-medium">{{ currency.code }}</span>
                            <span class="ml-2 text-gray-500">{{ currency.name }}</span>
                          </div>
                          <span v-if="currency.rate && currency.code !== 'KES'" class="text-xs text-gray-400">
                            {{ currency.rate.toFixed(4) }}
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                  <div class="border-t border-gray-100 p-2">
                    <button @click="refreshRates" :disabled="isLoading"
                      class="w-full text-xs text-gray-600 hover:text-gray-800 py-1 disabled:opacity-50">
                      {{ isLoading ? 'Updating...' : 'Refresh Rates' }}
                    </button>
                    <div v-if="lastUpdated" class="text-xs text-gray-400 text-center mt-1">
                      Updated: {{ formatLastUpdated }}
                    </div>
                  </div>
                </div>
              </transition>
            </teleport>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Messages -->
    <transition name="fade">
      <div v-if="message" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div :class="[
          'rounded p-2 text-xs flex justify-between items-center',
          messageType === 'error' ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-green-50 border border-green-200 text-green-800'
        ]">
          <div class="flex items-center">
            <span v-if="messageType === 'error'" class="mr-2">⚠️</span>
            <span v-else class="mr-2">✅</span>
            {{ message }}
          </div>
          <button @click="dismissMessage" class="font-semibold ml-2 hover:opacity-75">×</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useCurrencyStore } from '@/store/currencyStore';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';

export default {
  name: 'GlobalCurrencyBar',
  setup() {
    const currencyStore = useCurrencyStore();

    // Local state
    const showSelector = ref(false);
    const message = ref('');
    const messageType = ref('success');
    const isDetectingLocation = ref(false);
    const isDetectedFromLocation = ref(false);
    const selectorAnchor = ref(null);
    const dropdownRef = ref(null);
    const dropdownStyle = ref({});

    // Quick list of commonly used currencies
    const quickList = [
      { code: 'KES', name: 'Kenyan Shilling', flag: '🇰🇪', symbol: 'KSh' },
      { code: 'USD', name: 'US Dollar', flag: '🇺🇸', symbol: '$' },
      { code: 'EUR', name: 'Euro', flag: '🇪🇺', symbol: '€' },
      { code: 'GBP', name: 'British Pound', flag: '🇬🇧', symbol: '£' },
    ];

    // Computed properties
    const formatLastUpdated = computed(() => {
      if (!currencyStore.lastUpdated) return '';
      const date = new Date(currencyStore.lastUpdated);
      return date.toLocaleTimeString('en-KE', {
        hour: '2-digit',
        minute: '2-digit'
      });
    });

    // Enhanced currency selection methods with location tracking

    const toggleSelector = () => {
      showSelector.value = !showSelector.value;
      if (showSelector.value) {
        // position after DOM updates
        nextTick(() => updateDropdownPosition());
      }
    };

    const refreshRates = async () => {
      try {
        await currencyStore.refreshRates();
        showMessage('Exchange rates updated', 'success');
      } catch (error) {
        showMessage('Failed to update exchange rates', 'error');
      }
    };

    const showMessage = (text, type = 'success') => {
      message.value = text;
      messageType.value = type;
      setTimeout(() => {
        message.value = '';
      }, 4000);
    };

    const dismissMessage = () => {
      message.value = '';
    };

    // Fixed currency selection methods
    const selectQuick = async (currency) => {
      try {
        await currencyStore.selectCurrency(currency);
        localStorage.setItem('sanaapesa_manual_currency_selection', 'true');
        isDetectedFromLocation.value = false;
        showMessage(`Currency changed to ${currency.name}`, 'success');
        showSelector.value = false;
      } catch (error) {
        showMessage('Failed to change currency', 'error');
      }
    };

    const selectCurrency = async (currency) => {
      try {
        await currencyStore.selectCurrency(currency);
        localStorage.setItem('sanaapesa_manual_currency_selection', 'true');
        isDetectedFromLocation.value = false;
        showMessage(`Currency changed to ${currency.name}`, 'success');
        showSelector.value = false;
      } catch (error) {
        showMessage('Failed to change currency', 'error');
      }
    };

    // Location detection functionality
    const detectUserLocation = async () => {
      isDetectingLocation.value = true;

      try {
        // First try to get user's location via IP geolocation
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.country_code) {
          const detectedCurrency = getCurrencyFromCountry(data.country_code);
          if (detectedCurrency && detectedCurrency.code !== currencyStore.selectedCurrency.code) {
            // Only auto-set if user hasn't manually selected a currency before
            const hasManualSelection = localStorage.getItem('sanaapesa_manual_currency_selection');

            if (!hasManualSelection) {
              await currencyStore.selectCurrency(detectedCurrency);
              isDetectedFromLocation.value = true;
              showMessage(`Currency set to ${detectedCurrency.name} based on your location (${data.country_name})`, 'success');
            }
          }
        }
      } catch (error) {
        console.warn('Could not detect user location for currency:', error);
      } finally {
        isDetectingLocation.value = false;
      }
    };

    // Map country codes to currencies
    const getCurrencyFromCountry = (countryCode) => {
      // Prefer using the currency store's availableCurrencies to avoid hard-coded currency objects
      const mapping = {
        'KE': 'KES', 'US': 'USD', 'GB': 'GBP', 'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR', 'NL': 'EUR',
        'UG': 'UGX', 'TZ': 'TZS', 'RW': 'RWF', 'ET': 'ETB', 'ZA': 'ZAR', 'NG': 'NGN'
      };

      const code = mapping[countryCode];
      if (!code) return null;
      return currencyStore.availableCurrencies.find(c => c.code === code) || null;
    };



    // Close selector when clicking outside
    const handleClickOutside = (event) => {
      if (!showSelector.value) return;

      const clickedInsideAnchor = selectorAnchor.value && selectorAnchor.value.contains(event.target);
      const clickedInsideDropdown = dropdownRef.value && dropdownRef.value.contains(event.target);

      if (!clickedInsideAnchor && !clickedInsideDropdown) {
        showSelector.value = false;
      }
    };

    const updateDropdownPosition = () => {
      try {
        if (!selectorAnchor.value) return;
        const rect = selectorAnchor.value.getBoundingClientRect();
        const menuWidth = 256; // px
        const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
        const leftCandidate = rect.left + rect.width - menuWidth;
        const left = Math.min(Math.max(leftCandidate, 8), Math.max(viewportWidth - menuWidth - 8, 8));
        const top = rect.bottom + window.scrollY + 8; // 8px gap

        dropdownStyle.value = {
          position: 'absolute',
          top: `${top}px`,
          left: `${left}px`,
          width: `${menuWidth}px`,
          zIndex: 9999,
        };
      } catch (e) {
        // ignore positioning errors
      }
    };

    onMounted(async () => {
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, true);

      // Detect user location on first visit
      await detectUserLocation();

      // Show initial message if currency is not KES
      if (currencyStore.selectedCurrency.code !== 'KES') {
        const locationText = isDetectedFromLocation.value ? ' (auto-detected)' : '';
        showMessage(`All calculations shown in ${currencyStore.selectedCurrency.name}${locationText}`, 'success');
      }
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    });

    return {
      // Store state (using computed for proper reactivity)
      selectedCurrency: computed(() => currencyStore.selectedCurrency),
      availableCurrencies: computed(() => currencyStore.availableCurrencies),
      isLoading: computed(() => currencyStore.isLoading),
      lastUpdated: computed(() => currencyStore.lastUpdated),

      // Local state
      showSelector,
      message,
      messageType,
      quickList,
      isDetectingLocation,
      isDetectedFromLocation,

      // refs for positioning
      selectorAnchor,
      dropdownRef,
      dropdownStyle,

      // Computed
      formatLastUpdated,

      // Methods
      selectQuick,
      selectCurrency,
      toggleSelector,
      refreshRates,
      dismissMessage,
      updateDropdownPosition,
    };
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity .2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
</style>
